import { useState, useEffect } from 'react';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from 'recharts';
import api from '../api';
import { useLang } from '../context/LangContext';

const C_ELEC = '#2563eb';
const C_GAS  = '#ea580c';
const C_ENERGY = '#0d9488';
const C_NETWORK = '#6366f1';
const C_TAXES = '#f59e0b';

function fmt(n, dec = 0) {
  if (n == null || n === '') return '—';
  return Number(n).toLocaleString('nl-BE', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

// rows [{month, energy_type, ...}] → [{month, elektriciteit, gas}] op één veld
function pivotByType(rows, field) {
  const map = {};
  rows.forEach(r => {
    map[r.month] = map[r.month] || { month: r.month };
    map[r.month][r.energy_type] = r[field] == null ? null : Number(r[field]);
  });
  return Object.values(map).sort((a, b) => a.month.localeCompare(b.month));
}

// kosten optellen over energietypes per maand
function aggregateCosts(rows) {
  const map = {};
  rows.forEach(r => {
    map[r.month] = map[r.month] || { month: r.month, energy: 0, network: 0, taxes: 0 };
    map[r.month].energy  += Number(r.energy || 0);
    map[r.month].network += Number(r.network || 0);
    map[r.month].taxes   += Number(r.taxes || 0);
  });
  return Object.values(map).sort((a, b) => a.month.localeCompare(b.month));
}

function ChartCard({ title, children }) {
  return (
    <div className="chart-card mb-4">
      <div className="chart-title">{title}</div>
      <div style={{ padding: '12px 12px 4px' }}>{children}</div>
    </div>
  );
}

export default function Dashboard() {
  const { t } = useLang();
  const [summary, setSummary]   = useState([]);
  const [consumption, setCons]  = useState([]);
  const [costs, setCosts]       = useState([]);
  const [price, setPrice]       = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/api/analytics/summary'),
      api.get('/api/analytics/consumption'),
      api.get('/api/analytics/costs'),
      api.get('/api/analytics/unit-price'),
    ]).then(([s, c, k, p]) => {
      setSummary(s.data); setCons(c.data); setCosts(k.data); setPrice(p.data);
    }).finally(() => setLoading(false));
  }, []);

  // KPI's: bepaal laatste jaar met data
  const years = Array.from(new Set(summary.map(r => Number(r.year)))).sort((a, b) => b - a);
  const curY = years[0];
  const prevY = years[1];

  const sumFor = (year, field, type = null) =>
    summary.filter(r => Number(r.year) === year && (type ? r.energy_type === type : true))
           .reduce((acc, r) => acc + Number(r[field] || 0), 0);

  const costCur = sumFor(curY, 'cost');
  const costPrev = prevY ? sumFor(prevY, 'cost') : null;
  const costDelta = costPrev ? ((costCur - costPrev) / costPrev) * 100 : null;

  const elecCur = sumFor(curY, 'consumption', 'elektriciteit');
  const gasCur  = sumFor(curY, 'consumption', 'gas');

  const consData  = pivotByType(consumption, 'consumption_total');
  const costData  = aggregateCosts(costs);
  const priceData = pivotByType(price, 'unit_price');

  if (loading) {
    return (
      <>
        <div className="page-header"><h2>{t('dash_title')}</h2></div>
        <div className="page-body text-center text-muted py-5">
          <div className="spinner-border spinner-border-sm me-2" />{t('loading')}
        </div>
      </>
    );
  }

  const hasData = summary.length > 0;

  return (
    <>
      <div className="page-header"><h2>{t('dash_title')}</h2></div>
      <div className="page-body">
        {!hasData ? (
          <div className="card"><div className="card-body text-center text-muted py-5">{t('no_data')}</div></div>
        ) : (
          <>
            <div className="row g-3 mb-2">
              <div className="col-sm-6 col-xl-3">
                <div className="kpi-card">
                  <div className="kpi-label">{t('kpi_consumption_year')} {curY}</div>
                  <div className="kpi-value">{fmt(elecCur)} <small className="text-muted" style={{ fontSize: 14 }}>kWh</small></div>
                  <div className="text-muted-sm">{fmt(gasCur)} m³ gas</div>
                </div>
              </div>
              <div className="col-sm-6 col-xl-3">
                <div className="kpi-card">
                  <div className="kpi-label">{t('kpi_cost_year')} {curY}</div>
                  <div className="kpi-value">€ {fmt(costCur, 0)}</div>
                  {costDelta != null && (
                    <div className={`kpi-delta ${costDelta >= 0 ? 'up' : 'down'}`}>
                      <i className={`bi bi-arrow-${costDelta >= 0 ? 'up' : 'down'}`} />
                      {fmt(Math.abs(costDelta), 1)}% {t('vs_last_year')}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-sm-6 col-xl-3">
                <div className="kpi-card">
                  <div className="kpi-label">{t('kpi_avg_price')} · elek</div>
                  <div className="kpi-value">
                    € {elecCur ? fmt(sumFor(curY, 'cost', 'elektriciteit') / elecCur, 3) : '—'}
                    <small className="text-muted" style={{ fontSize: 13 }}>/kWh</small>
                  </div>
                </div>
              </div>
              <div className="col-sm-6 col-xl-3">
                <div className="kpi-card">
                  <div className="kpi-label">{t('kpi_invoices')} {curY}</div>
                  <div className="kpi-value">{fmt(sumFor(curY, 'invoices'))}</div>
                </div>
              </div>
            </div>

            <ChartCard title={t('chart_monthly_consumption')}>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={consData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="month" fontSize={12} />
                  <YAxis fontSize={12} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="elektriciteit" name="Elektriciteit (kWh)" stroke={C_ELEC} dot={false} strokeWidth={2} />
                  <Line type="monotone" dataKey="gas" name="Gas (m³)" stroke={C_GAS} dot={false} strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            <div className="row">
              <div className="col-xl-7">
                <ChartCard title={t('chart_monthly_cost')}>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={costData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                      <XAxis dataKey="month" fontSize={12} />
                      <YAxis fontSize={12} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="energy"  name="Energie" stackId="a" fill={C_ENERGY} />
                      <Bar dataKey="network" name="Netkosten" stackId="a" fill={C_NETWORK} />
                      <Bar dataKey="taxes"   name="Heffingen" stackId="a" fill={C_TAXES} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </div>
              <div className="col-xl-5">
                <ChartCard title={t('chart_unit_price')}>
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={priceData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                      <XAxis dataKey="month" fontSize={12} />
                      <YAxis fontSize={12} />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="elektriciteit" name="€/kWh" stroke={C_ELEC} dot={false} strokeWidth={2} />
                      <Line type="monotone" dataKey="gas" name="€/m³" stroke={C_GAS} dot={false} strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartCard>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
